import { Close } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { StyleObj } from '../../../../@types';
import { removeBetslipBet } from '../../../../stores/betslip/selectors';
import type { BetslipEvent } from '../../../../types/betslip';
import { useBetBuilderItem } from '../../../../hooks/useBetBuilderItem';
import useLocalization from '../../../../hooks/useLocalization';
import BetBuilderLegList from '../../../molecules/BetBuilderLegList';
import OddsDisplay from '../../../atoms/OddsDisplay';

const styles: StyleObj = {
  container: {
    backgroundColor: 'neutral.100',
    borderTop: '1px solid',
    borderColor: 'neutral.200',
    px: 1,
    py: 0.75,
    gap: 0.75,
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1,
  },
  headerContent: {
    minWidth: 0,
    textAlign: 'left',
    gap: 0.25,
  },
  title: {
    color: 'neutral.800',
    lineHeight: 1.2,
  },
  eventName: {
    color: 'neutral.600',
    lineHeight: 1.2,
  },
  removeButton: {
    p: 0.25,
    color: 'neutral.600',
    alignSelf: 'flex-start',
  },
  removeIcon: {
    fontSize: 16,
  },
};

type BetBuilderBetslipItemProps = {
  bet: BetslipEvent;
  onRemoveBet?: (outcomeId: string) => void;
};

const BetBuilderBetslipItem = ({ bet, onRemoveBet }: BetBuilderBetslipItemProps) => {
  const { t } = useLocalization();
  const { legs } = useBetBuilderItem({ type: 'betslip', bet });

  if (!bet.betBuilder) {
    return null;
  }

  const handleRemoveLeg = (outcomeId: string) => {
    removeBetslipBet(outcomeId);
    onRemoveBet?.(outcomeId);
  };

  return (
    <Stack sx={styles.container} data-testid={`gola-bet-builder-item-${bet.eventId}`}>
      <Stack direction='row' sx={styles.header}>
        <Stack sx={styles.headerContent}>
          <Typography variant='subtitle2' sx={styles.title}>
            {bet.eventName}
          </Typography>
          <Typography variant='body3' sx={styles.eventName}>
            {t('betBuilder')}
          </Typography>
        </Stack>
        <OddsDisplay
          value={bet.odds}
          variant='body2'
          sx={{
            color: 'neutral.800',
          }}
        />
      </Stack>

      <BetBuilderLegList
        legs={legs}
        variant='gola'
        renderAction={(leg) => (
          <IconButton
            key={leg.outcomeId}
            size='small'
            sx={styles.removeButton}
            onClick={() => handleRemoveLeg(leg.outcomeId)}
            aria-label={t('remove')}
            data-testid={`gola-remove-bet-builder-leg-${leg.outcomeId}`}
          >
            <Close sx={styles.removeIcon} />
          </IconButton>
        )}
      />
    </Stack>
  );
};

export default BetBuilderBetslipItem;
